import React, {
  HTMLInputTypeAttribute,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Control,
  FieldError,
  FieldValues,
  Path,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ErrorMessage } from '@hookform/error-message';
import WarningIconSm from 'components/Icons/WarningIconSm';
import theme from 'theme/theme';

import { ErrorText, FormLabel, InputContainer } from './StyledFormInputs';

export const FOCUS_DELAY = 500;

type StandardInputProps<TFormValues extends FieldValues> = {
  label: string;
  inputId: Path<TFormValues>;
  control: Control<TFormValues>;
  error?: FieldError;
  inputType?: HTMLInputTypeAttribute;
  register: UseFormRegister<TFormValues>;
};

const StandardInput = <TFormValues extends Record<string, unknown>>({
  label,
  error,
  inputId,
  control,
  inputType = 'text',
  register,
}: StandardInputProps<TFormValues>) => {
  const [isFocused, setIsFocus] = useState(false);
  const refEl = useRef<HTMLInputElement | null>(null);
  const { ref, ...rest } = register(inputId);

  const inputValue = useWatch({
    control,
    name: inputId,
  });

  useEffect(() => {
    const focusDelay = setTimeout(() => {
      try {
        if (refEl?.current?.matches('*:-webkit-autofill')) {
          setIsFocus(true);
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }, FOCUS_DELAY);

    return () => clearTimeout(focusDelay);
  }, [inputId]);

  return (
    <InputContainer $error={!!error}>
      <FormLabel
        htmlFor={inputId}
        $error={!!error}
        $active={isFocused || !!inputValue || !!error}
      >
        {label}
      </FormLabel>
      <FormInput
        type={inputType}
        id={inputId}
        $error={!!error}
        {...rest}
        ref={(e) => {
          ref(e);
          refEl.current = e;
        }}
        onFocus={() => setIsFocus(true)}
      />

      <ErrorMessage
        name={inputId}
        errors={{ [inputId]: error }}
        render={({ message }) => (
          <ErrorText $error={!!error}>
            <WarningIconSm color={theme.colors.primary} />
            {message}
          </ErrorText>
        )}
      />
    </InputContainer>
  );
};

export const autofillInputField = css<{ $error?: boolean }>`
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    /* stylelint-disable property-no-vendor-prefix, comment-empty-line-before */
    -webkit-text-fill-color: ${({ $error, theme }) =>
      $error ? theme.colors.lightRed : theme.colors.darkBlueV2};
    -webkit-transition-delay: 9999s;
    transition-delay: 9999s;
  }
`;

const FormInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 22px;
  margin-top: 22px;
  margin-bottom: 7px;
  font-size: 16px;
  outline: none;
  border: none;
  border-radius: 5px;
  background: none;
  padding: 0 19px 0 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  font-weight: 300;
  color: ${({ $error, theme }) =>
    $error ? theme.colors.lightRed : theme.colors.darkBlueV2};
  ${autofillInputField}

  @media (width <= 450px) {
    padding: 0 10px 0 0;
  }

  @media (width <= 300px) {
    font-size: 14px;
  }
`;

export default memo(StandardInput) as typeof StandardInput;
